import { useEffect } from "react";
import { Mail, Phone, Instagram, Linkedin, MapPin } from "lucide-react";
import PageHero from "@/components/PageHero";
import InquiryForm from "@/components/InquiryForm";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { SITE } from "@/data/content";

export default function Contact() {
  useEffect(() => {
    document.title = "Contact — Jen Cullerton Johnson";
  }, []);
  return (
    <>
      <PageHero
        eyebrow="Contact"
        title={["I'd love to", <span key="d"><em className="text-terra">hear</em> from you</span>]}
        lede="Whether you're a young person, teacher, parent, event organizer, librarian, environmentalist, or reader — reach out any way you'd like."
        meta="Author visits · Workshops & speaking · Book orders & media · Just to say hello"
      />

      <section className="py-20 lg:py-28" data-testid="contact-main">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow className="mb-8">Reach out directly</Eyebrow>
              <div className="space-y-6" data-testid="contact-details">
                <a href={`mailto:${SITE.email}`} className="group flex items-center gap-4 border border-line rounded-lg p-5 hover:bg-cream transition-colors duration-300" data-testid="contact-email-card">
                  <span className="w-11 h-11 rounded-full bg-goldsoft text-terra flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-txtmut mb-1">Email</span>
                    <span className="block font-serif text-lg text-ink group-hover:text-terra transition-colors break-all">{SITE.email}</span>
                  </span>
                </a>
                <a href={SITE.phoneHref} className="group flex items-center gap-4 border border-line rounded-lg p-5 hover:bg-cream transition-colors duration-300" data-testid="contact-phone-card">
                  <span className="w-11 h-11 rounded-full bg-goldsoft text-terra flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-txtmut mb-1">Google Voice</span>
                    <span className="block font-serif text-lg text-ink group-hover:text-terra transition-colors">{SITE.phone}</span>
                  </span>
                </a>
                <div className="flex items-center gap-4 border border-line rounded-lg p-5" data-testid="contact-location-card">
                  <span className="w-11 h-11 rounded-full bg-goldsoft text-terra flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-txtmut mb-1">Based in</span>
                    <span className="block font-serif text-lg text-ink">{SITE.location}</span>
                  </span>
                </div>
                <div className="flex gap-3" data-testid="contact-socials">
                  <a href="https://www.instagram.com/jencullertonjohnson/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex-1 inline-flex items-center justify-center gap-2 border border-line rounded-lg py-4 text-ink hover:bg-cream transition-colors duration-300" data-testid="contact-social-instagram">
                    <Instagram className="w-5 h-5" /> <span className="font-mono text-[11px] uppercase tracking-[0.14em]">Instagram</span>
                  </a>
                  <a href="https://www.linkedin.com/in/jen-cullerton-johnson-a278ba9b" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="flex-1 inline-flex items-center justify-center gap-2 border border-line rounded-lg py-4 text-ink hover:bg-cream transition-colors duration-300" data-testid="contact-social-linkedin">
                    <Linkedin className="w-5 h-5" /> <span className="font-mono text-[11px] uppercase tracking-[0.14em]">LinkedIn</span>
                  </a>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 rounded-lg overflow-hidden border border-line hidden lg:block">
                <img src="/images/jen-contact.png" alt="Moments from Jen's events — storytelling, tree planting, and workshops" className="w-full h-56 object-cover photo-tone" data-testid="contact-photo" />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="border border-line rounded-lg bg-paper p-6 sm:p-10 shadow-sm" data-testid="contact-form-card">
              <h2 className="font-serif text-3xl text-ink mb-2">Send a message</h2>
              <p className="text-txtmut text-sm mb-8">Once you hit Send, Jen will get back to you as soon as she can. Looking forward to connecting!</p>
              <InquiryForm defaultInquiry="other" idPrefix="contact" testid="contact-inquiry-form" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
